import React, { useState, useEffect, useMemo } from 'react';
import { db, auth } from '../utils/firebase';
import { doc, getDoc, setDoc, deleteDoc, collection, getDocs } from 'firebase/firestore';
import { onAuthStateChanged, deleteUser } from 'firebase/auth';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import BottomTabBar from '../components/BottomTabBar';
import ProfileForm from '../components/ProfileForm';
import ActivitySettings from '../components/ActivitySettings';
import ModalBackdrop from '../components/ModalBackdrop';
import '../styles/user.css';
import '../styles/buttons.css';
import '../styles/account.css';
import logger from '../utils/logger';
import { useToast } from '../context/ToastContext';
import { calculateCaloriesBurned } from '../utils/calories';
import { toMillis } from '../utils/timestamps';
import * as XLSX from 'xlsx';

// Bump this whenever the Help/What's New content changes. Users whose saved
// lastHelpVersion is lower than this see an orange "unread" dot on the "?"
// button until they open the modal, at which point lastHelpVersion is written
// back to their user doc and the dot clears.
const HELP_VERSION = 2;

function Account() {
  const navigate = useNavigate();
  const location = useLocation();
  const showToast = useToast();
  const [userId, setUserId] = useState(null);
  const [profile, setProfile] = useState({
    displayName: '',
    email: '',
    phoneNumber: '',
    firstName: '',
    lastName: '',
    gender: 'Male',
    dateOfBirth: '',
    height: '',
    bodyFatPercentage: '',
    hideExerciseInfo: false,
  });
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activities, setActivities] = useState({
    weightlifting: {},
    cardio: {},
    bodyweight: {},
    flexibility: {},
  });
  const [activityLoading, setActivityLoading] = useState(false);
  // One-shot flag: starts false, flips to true after the initial auth-effect
  // fetch completes, and never flips back. Used as the visibility gate for
  // the profile/goal/activity forms so that save operations don't unmount
  // and remount the forms (which looked like a page navigation flash).
  // The loading/goalLoading/activityLoading flags remain for their existing
  // purpose — disabling buttons and showing "Saving..." text.
  const [initialDataLoaded, setInitialDataLoaded] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState({
    weightlifting: false,
    cardio: false,
    bodyweight: false,
    flexibility: false,
  });
  const [showPrivacyPopup, setShowPrivacyPopup] = useState(false);
  const [showDeleteAccountConfirm, setShowDeleteAccountConfirm] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [helpTab, setHelpTab] = useState('howto');
  const [helpUnread, setHelpUnread] = useState(false);

  const allActivities = useMemo(() => {
    const activities = {
      weightlifting: {
        arnoldShoulderPress: 'Arnold Shoulder Press',
        barbellRows: 'Barbell Rows',
        barbellShoulderPress: 'Barbell Shoulder Press',
        benchPress: 'Bench Press',
        bicepCurls: 'Bicep Curls',
        calfRaises: 'Calf Raises',
        deadlifts: 'Deadlifts',
        declineBenchPress: 'Decline Bench Press',
        dumbbellFlies: 'Dumbbell Flies',
        dumbbellRows: 'Dumbbell Rows',
        frontDeltRaise: 'Front Delt Raise',
        hammerCurls: 'Hammer Curls',
        inclineBenchPress: 'Incline Bench Press',
        isoLateralHighRow: 'Iso-Lateral High Row',
        latPulldown: 'Lat Pulldown',
        lateralDeltRaise: 'Lateral Delt Raise',
        legCurl: 'Leg Curl',
        legExtension: 'Leg Extension',
        legPress: 'Leg Press',
        lunges: 'Lunges',
        overheadPress: 'Overhead Press',
        rearDeltFly: 'Rear Delt Fly',
        seatedCableRow: 'Seated Cable Row',
        skullCrushers: 'Skull Crushers',
        squats: 'Squats',
        tricepDips: 'Tricep Dips',
        zottmanCurls: 'Zottman Curls',
      },
      cardio: {
        aerobics: 'Aerobics',
        boxing: 'Boxing',
        crossCountrySkiing: 'Cross-Country Skiing',
        cycling: 'Cycling',
        dancing: 'Dancing',
        elliptical: 'Elliptical',
        hiking: 'Hiking',
        jogging: 'Jogging',
        jumpRope: 'Jump Rope',
        kickboxing: 'Kickboxing',
        rowing: 'Rowing',
        running: 'Running',
        skating: 'Skating',
        spinning: 'Spinning',
        sprinting: 'Sprinting',
        stairClimbing: 'Stair Climbing',
        swimming: 'Swimming',
        walking: 'Walking',
        zumba: 'Zumba',
      },
      bodyweight: {
        bearCrawls: 'Bear Crawls',
        burpees: 'Burpees',
        chinUps: 'Chin-Ups',
        crunches: 'Crunches',
        dips: 'Dips',
        highKnees: 'High Knees',
        jumpingJacks: 'Jumping Jacks',
        legRaises: 'Leg Raises',
        lungesBodyweight: 'Lunges (Bodyweight)',
        mountainClimbers: 'Mountain Climbers',
        plank: 'Plank',
        pullUps: 'Pull-Ups',
        pushUps: 'Push-Ups',
        sidePlank: 'Side Plank',
        sitUps: 'Sit-Ups',
        squatJumps: 'Squat Jumps',
        stepUps: 'Step-Ups',
        wallSits: 'Wall Sits',
      },
      flexibility: {
        balanceExercises: 'Balance Exercises',
        catCowStretch: 'Cat-Cow Stretch',
        childPose: 'Child’s Pose',
        cobraPose: 'Cobra Pose',
        downwardDog: 'Downward Dog',
        dynamicStretching: 'Dynamic Stretching',
        foamRolling: 'Foam Rolling',
        functionalMovementDrills: 'Functional Movement Drills',
        hamstringStretch: 'Hamstring Stretch',
        hipOpeners: 'Hip Openers',
        mobilityDrills: 'Mobility Drills',
        pigeonPose: 'Pigeon Pose',
        pilates: 'Pilates',
        seatedForwardBend: 'Seated Forward Bend',
        shoulderMobility: 'Shoulder Mobility',
        stretching: 'Stretching',
        taiChi: 'Tai Chi',
        yoga: 'Yoga',
      },
    };
    return activities;
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        setUserId(user.uid);
        setProfile((prev) => ({ ...prev, email: user.email || '' }));
  
        setLoading(true);
        setActivityLoading(true);
        try {
          const userDoc = await getDoc(doc(db, 'users', user.uid));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            setProfile((prev) => ({
              ...prev,
              displayName: userData.displayName || '',
              phoneNumber: userData.phoneNumber || '',
              firstName: userData.firstName || '',
              lastName: userData.lastName || '',
              gender: userData.gender || 'Male',
              dateOfBirth: userData.dateOfBirth || '',
              height: userData.height?.toString() || '',
              bodyFatPercentage: userData.bodyFatPercentage?.toString() || '',
              hideExerciseInfo: userData.hideExerciseInfo || false,
            }));
            // Show the unread dot if the user hasn't seen the current Help
            // content yet. Missing field → treat as version 0.
            const lastHelpVersion = userData.lastHelpVersion || 0;
            if (lastHelpVersion < HELP_VERSION) setHelpUnread(true);
          }
          const activitiesDoc = await getDoc(doc(db, 'users', user.uid, 'preferences', 'activities'));
          const defaultActivities = {
            weightlifting: {},
            cardio: {},
            bodyweight: {},
            flexibility: {},
          };
          if (activitiesDoc.exists()) {
            const savedData = activitiesDoc.data().activities;
            const normalizeActivity = (activity) => activity.trim().toLowerCase().replace(/[-\s]+/g, ' ');
            if (Array.isArray(savedData)) {
              // Legacy format: array of activity name strings → migrate to { home, gym }
              const normalizedSavedActivities = savedData.map(normalizeActivity);
              Object.keys(allActivities).forEach(category => {
                Object.keys(allActivities[category]).forEach(activity => {
                  const activityName = allActivities[category][activity];
                  const normalizedActivityName = normalizeActivity(activityName);
                  const isSelected = normalizedSavedActivities.includes(normalizedActivityName);
                  defaultActivities[category][activity] = isSelected
                    ? { home: true, gym: true }
                    : { home: false, gym: false };
                });
              });
            } else if (savedData && typeof savedData === 'object') {
              // New format: object keyed by activity name → { home, gym } or legacy boolean
              Object.keys(allActivities).forEach(category => {
                Object.keys(allActivities[category]).forEach(activity => {
                  const activityName = allActivities[category][activity];
                  const normalizedActivityName = normalizeActivity(activityName);
                  const match = Object.entries(savedData).find(
                    ([key]) => normalizeActivity(key) === normalizedActivityName
                  );
                  if (match) {
                    const val = match[1];
                    if (typeof val === 'boolean') {
                      defaultActivities[category][activity] = val
                        ? { home: true, gym: true }
                        : { home: false, gym: false };
                    } else {
                      defaultActivities[category][activity] = {
                        home: !!val.home,
                        gym: !!val.gym,
                      };
                    }
                  } else {
                    defaultActivities[category][activity] = { home: false, gym: false };
                  }
                });
              });
            }
          } else {
            Object.keys(allActivities).forEach(category => {
              Object.keys(allActivities[category]).forEach(activity => {
                defaultActivities[category][activity] = { home: true, gym: true };
              });
            });
          }
          setActivities(defaultActivities);
        } catch (err) {
          logger.error(err, { page: 'Account', action: 'loadProfileAndPreferences' });
          setError('Failed to load profile or preferences. Please try again.');
        } finally {
          setLoading(false);
          setActivityLoading(false);
          setInitialDataLoaded(true);
        }
      } else {
        navigate('/login');
      }
    });
    return () => unsubscribe();
  }, [navigate, allActivities]);

  // Opens the Help modal and marks the current HELP_VERSION as seen by
  // writing it back to the user doc. The unread dot clears immediately for
  // instant UI feedback — the Firestore write is fire-and-forget.
  const handleOpenHelp = async () => {
    setShowHelp(true);
    setHelpUnread(false);
    if (userId) {
      try {
        await setDoc(doc(db, 'users', userId), { lastHelpVersion: HELP_VERSION }, { merge: true });
      } catch (err) {
        logger.error(err, { page: 'Account', action: 'markHelpSeen' });
      }
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!profile.displayName.trim()) {
      setError('Display Name is required.');
      return;
    }
    const height = parseFloat(profile.height);
    const dob = profile.dateOfBirth;
    if (height && (isNaN(height) || height <= 0)) {
      setError('Height must be a positive number.');
      return;
    }
    if (dob) {
      const birthDate = new Date(dob);
      const today = new Date();
      if (birthDate > today) {
        setError('Date of birth cannot be in the future.');
        return;
      }
    }
  
    setLoading(true);
    try {
      const profileData = {
        displayName: profile.displayName.trim(),
        phoneNumber: profile.phoneNumber.trim(),
        firstName: profile.firstName.trim(),
        lastName: profile.lastName.trim(),
        email: profile.email,
        gender: profile.gender,
        dateOfBirth: profile.dateOfBirth,
        height: height || 0,
        bodyFatPercentage: parseFloat(profile.bodyFatPercentage) > 0 ? parseFloat(profile.bodyFatPercentage) : null,
        hideExerciseInfo: profile.hideExerciseInfo,
      };
      await setDoc(doc(db, 'users', userId), profileData, { merge: true });
      setError(null);
      showToast('Profile saved successfully!', 'success');
    } catch (err) {
      logger.error(err, { page: 'Account', action: 'saveProfile' });
      setError('Failed to save profile. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAccount = () => {
    setShowDeleteAccountConfirm(true);
  };

  const confirmDeleteAccount = async () => {
    setShowDeleteAccountConfirm(false);
    setLoading(true);
    const subcollections = ['reps', 'weightRecords', 'posts', 'preferences'];
    const deleteErrors = [];

    for (const subcollection of subcollections) {
      try {
        const snapshot = await getDocs(collection(db, 'users', userId, subcollection));
        await Promise.all(snapshot.docs.map(d => deleteDoc(d.ref)));
      } catch (err) {
        logger.error(err, { page: 'Account', action: 'deleteSubcollection', subcollection });
        deleteErrors.push(subcollection);
      }
    }

    if (deleteErrors.length > 0) {
      setError(`Failed to delete some account data (${deleteErrors.join(', ')}). Please try again.`);
      setLoading(false);
      return;
    }

    try {
      await deleteDoc(doc(db, 'users', userId));
      await deleteUser(auth.currentUser);
      navigate('/login');
    } catch (err) {
      logger.error(err, { page: 'Account', action: 'deleteAccount' });
      setError('Failed to delete account. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleActivityToggle = (category, activity, location) => {
    setActivities(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [activity]: {
          ...prev[category][activity],
          [location]: !prev[category][activity][location],
        },
      },
    }));
  };

  const handleSelectAll = (category, location) => {
    setActivities(prev => ({
      ...prev,
      [category]: Object.keys(prev[category]).reduce((acc, activity) => {
        acc[activity] = { ...prev[category][activity], [location]: true };
        return acc;
      }, {}),
    }));
  };

  const handleDeselectAll = (category, location) => {
    setActivities(prev => ({
      ...prev,
      [category]: Object.keys(prev[category]).reduce((acc, activity) => {
        acc[activity] = { ...prev[category][activity], [location]: false };
        return acc;
      }, {}),
    }));
  };

  const handleToggleCategory = (category) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category],
    }));
  };

  const handleSaveActivities = async () => {
    if (!userId) return;
    setActivityLoading(true);
    try {
      const activitiesObj = {};
      Object.entries(activities).forEach(([category, activitiesMap]) => {
        Object.entries(activitiesMap).forEach(([activityKey, locations]) => {
          const name = allActivities[category][activityKey].trim();
          activitiesObj[name] = { home: !!locations.home, gym: !!locations.gym };
        });
      });
      const activitiesRef = doc(db, 'users', userId, 'preferences', 'activities');
      await setDoc(activitiesRef, { activities: activitiesObj });
      setError(null);
      showToast('Activity preferences saved successfully!', 'success');
    } catch (err) {
      logger.error(err, { page: 'Account', action: 'saveActivityPreferences' });
      setError('Failed to save activity preferences. Please try again.');
    } finally {
      setActivityLoading(false);
    }
  };

  const handleExportData = async () => {
    try {
      const [repsSnap, mealsSnap, weightSnap] = await Promise.all([
        getDocs(collection(db, 'users', userId, 'reps')),
        getDocs(collection(db, 'users', userId, 'meals')),
        getDocs(collection(db, 'users', userId, 'weightRecords')),
      ]);

      const activitiesData = repsSnap.docs.map(d => {
        const data = d.data();
        return {
          Date: new Date(toMillis(data.timestamp)).toLocaleDateString('en-US'),
          Activity: data.activityType || '',
          Reps: data.count || 0,
          'Time (min)': data.time || 0,
          'Weight Lifted (lbs)': data.weightLifted || 0,
          'Calories Burned': data.caloriesBurned != null ? data.caloriesBurned : calculateCaloriesBurned(data, profile.currentWeight),
        };
      }).sort((a, b) => new Date(b.Date) - new Date(a.Date));

      const mealsData = mealsSnap.docs.map(d => {
        const data = d.data();
        const src = data.source || '';
        return {
          Date: new Date(toMillis(data.timestamp)).toLocaleDateString('en-US'),
          'Food Name': data.foodName || '',
          Calories: data.calories || 0,
          Source: src === 'usda' ? 'USDA' : src ? src.charAt(0).toUpperCase() + src.slice(1) : 'Manual',
        };
      }).sort((a, b) => new Date(b.Date) - new Date(a.Date));

      const weightData = weightSnap.docs.map(d => {
        const data = d.data();
        return {
          Date: new Date(toMillis(data.timestamp)).toLocaleDateString('en-US'),
          'Weight (lbs)': data.weight || 0,
        };
      }).sort((a, b) => new Date(b.Date) - new Date(a.Date));

      const wb = XLSX.utils.book_new();
      const colWidth = { wch: 18 };
      const wsActivities = XLSX.utils.json_to_sheet(activitiesData);
      wsActivities['!cols'] = Object.keys(activitiesData[0] || {}).map(() => colWidth);
      XLSX.utils.book_append_sheet(wb, wsActivities, 'Activities');
      const wsMeals = XLSX.utils.json_to_sheet(mealsData);
      const mealCols = mealsData.length > 0
        ? Object.keys(mealsData[0]).map((_, i) => ({
            wch: Math.min(75, Math.max(18, ...mealsData.map(row => String(Object.values(row)[i] ?? '').length)))
          }))
        : [];
      wsMeals['!cols'] = mealCols;
      XLSX.utils.book_append_sheet(wb, wsMeals, 'Meals');
      const wsWeight = XLSX.utils.json_to_sheet(weightData);
      wsWeight['!cols'] = Object.keys(weightData[0] || {}).map(() => colWidth);
      XLSX.utils.book_append_sheet(wb, wsWeight, 'Weight');
      const today = new Date().toISOString().split('T')[0];
      XLSX.writeFile(wb, `friendsfitness-export-${today}.xlsx`);
    } catch (err) {
      logger.error(err, { page: 'Account', action: 'exportData' });
      setError('Failed to export data. Please try again.');
    }
  };

  useEffect(() => {
    if (error) {
      showToast(error, 'error');
      setError(null);
    }
  }, [error, showToast]);

  return (
    <div className="App">
      <nav className="nav-desktop">
        <Link to="/user" className="nav-brand">FriendsFitness</Link>
        <div className="nav-links-right">
          <Link to="/user" className={`nav-pill${location.pathname === '/user' ? ' active-link' : ''}`}>User</Link>
          <Link to="/stats" className={`nav-pill${location.pathname === '/stats' ? ' active-link' : ''}`}>Stats</Link>
          <Link to="/community" className={`nav-pill${location.pathname === '/community' ? ' active-link' : ''}`}>Community</Link>
          <span style={{ position: 'relative', display: 'inline-block' }}>
            <Link to="/account" className={`nav-pill${location.pathname === '/account' ? ' active-link' : ''}`}>Account</Link>
            {helpUnread && <span aria-hidden="true" style={{ position: 'absolute', top: '4px', right: '4px', width: '8px', height: '8px', borderRadius: '50%', background: '#f97316' }} />}
          </span>
          <button className="logout-button" onClick={() => auth.signOut().then(() => navigate('/login'))}>Logout</button>
        </div>
      </nav>
      <BottomTabBar helpUnread={helpUnread} />
      <div className="help-header-row">
        <h2 style={{ margin: 0, color: '#f1f5f9' }}>Account</h2>
        <div className="help-trigger-wrap">
          <button
            type="button"
            className="help-trigger-btn"
            onClick={handleOpenHelp}
            aria-label="Open help"
            title="Help"
          >
            ?
          </button>
          {helpUnread && <span className="help-unread-dot" aria-hidden="true" />}
        </div>
      </div>
      <ProfileForm
        profile={profile}
        setProfile={setProfile}
        handleSave={handleSave}
        loading={loading}
        showPrivacyPopup={showPrivacyPopup}
        setShowPrivacyPopup={setShowPrivacyPopup}
        initialDataLoaded={initialDataLoaded}
        handleDeleteAccount={handleDeleteAccount}
        showDeleteAccountConfirm={showDeleteAccountConfirm}
        setShowDeleteAccountConfirm={setShowDeleteAccountConfirm}
        confirmDeleteAccount={confirmDeleteAccount}
      />
      <ActivitySettings
        activities={activities}
        allActivities={allActivities}
        expandedCategories={expandedCategories}
        activityLoading={activityLoading}
        handleActivityToggle={handleActivityToggle}
        handleSelectAll={handleSelectAll}
        handleDeselectAll={handleDeselectAll}
        handleToggleCategory={handleToggleCategory}
        handleSaveActivities={handleSaveActivities}
        initialDataLoaded={initialDataLoaded}
      />
      <div className="weight-tracking" style={{ textAlign: 'center', maxWidth: '660px', margin: '0 auto 24px' }}>
        <h3>Data</h3>
        <p className="goal-tagline">Download all your meals, activities, and weight records as an Excel workbook.</p>
        <button className="btn-primary" onClick={handleExportData}>
          Export My Data
        </button>
      </div>
      {showHelp && (
        <ModalBackdrop onClose={() => setShowHelp(false)}>
          <div className="popup-content help-modal-content">
            <div className="help-modal-header">
              <h3 style={{ margin: 0 }}>FriendsFitness Help</h3>
              <button
                type="button"
                className="btn-secondary"
                onClick={() => setShowHelp(false)}
                aria-label="Close help"
              >
                Close
              </button>
            </div>
            <div className="help-tabs" role="tablist">
              <button
                type="button"
                role="tab"
                aria-selected={helpTab === 'howto'}
                className={`help-tab-btn${helpTab === 'howto' ? ' active' : ''}`}
                onClick={() => setHelpTab('howto')}
              >
                How To
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={helpTab === 'whatsnew'}
                className={`help-tab-btn${helpTab === 'whatsnew' ? ' active' : ''}`}
                onClick={() => setHelpTab('whatsnew')}
              >
                What's New
              </button>
            </div>
            {helpTab === 'howto' && (
              <div className="help-tab-content">
                <div className="help-section">
                  <h4>Logging Activities</h4>
                  <p>
                    Tap any activity in the Recent Activities list to quickly re-log it without searching. Use the Home/Gym toggle to switch between your home and gym exercise lists &mdash; customize which activities appear in each list from the Account page.
                  </p>
                </div>
                <div className="help-section">
                  <h4>Logging Food</h4>
                  <p>
                    Tap any pill in the Recent row above the search bar to instantly pull up that food's serving picker. For new foods, search by name and select from the results. If nothing matches, expand "Or enter manually" to log any food by name and calories directly.
                  </p>
                </div>
                <div className="help-section">
                  <h4>Pinning Meals</h4>
                  <p>
                    Long-press any pill in the Recent row to pin it. Pinned items appear in a separate Pinned row above Recent and stay there permanently until unpinned. Tap a pinned item to log it just like a recent item. Long-press a pinned item to unpin it. Up to 6 meals can be pinned at once.
                  </p>
                </div>
                <div className="help-section">
                  <h4>Food Photo Scan</h4>
                  <p>
                    Tap the camera icon next to the search bar to scan a photo of your food. The AI will identify the food and estimate calories. If it finds a match in the food database you can select it normally. If not &mdash; common for restaurant dishes and combo meals &mdash; tap the orange "Log AI estimate" link to pre-fill the manual entry form with the AI's best guess. Review and tap Log to save it.
                  </p>
                </div>
                <div className="help-section">
                  <h4>Energy Balance</h4>
                  <p>
                    The Energy Balance card shows your daily calorie budget based on your BMR, calories burned from logged activities, and calories consumed from logged meals. It updates automatically as you log throughout the day.
                  </p>
                </div>
                <div className="help-section">
                  <h4>Setting a Weight Goal</h4>
                  <p>
                    On the User page, find the Profile card and enter your target weight and target date. FriendsFitness will track your progress toward that goal over time.
                  </p>
                </div>
                <div className="help-section">
                  <h4>Customizing Your Activity Lists</h4>
                  <p>
                    On the Account page, go to Activity Preferences to choose which activities appear in your Home and Gym lists. Toggle each activity on or off for each environment independently.
                  </p>
                </div>
                <div className="help-section">
                  <h4>Setting Goals</h4>
                  <p>
                    On the Account page, set your daily rep or time goal. This goal tracks your streak and appears on the User page as a progress indicator.
                  </p>
                </div>
              </div>
            )}
            {helpTab === 'whatsnew' && (
              <div className="help-tab-content">
                <div className="help-section">
                  <h4>April 2026</h4>
                  <ul>
                    <li>Smarter food search &mdash; search results now rank the closest name matches first</li>
                    <li>AI food scan improvements &mdash; after scanning, tap the orange "Log AI estimate" link to pre-fill the manual entry form with the AI's identified food and calorie estimate. Useful for restaurant dishes and combo meals not in the food database</li>
                    <li>Cleaner food log state &mdash; selecting a recent meal, scanning a photo, and searching now all properly reset each other so you never see conflicting options at the same time</li>
                    <li>Pinned meals &mdash; long-press (or right-click on desktop) any Recent pill to pin it. Pinned items appear above Recent for fast access and persist across sessions. Long-press a pinned item to unpin it. Up to 6 items can be pinned.</li>
                  </ul>
                </div>
              </div>
            )}
          </div>
        </ModalBackdrop>
      )}
    </div>
  );
}

export default Account;